import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Typography,
  IconButton,
  Rating,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Seo from "../components/Seo";
import TrustPanel from "../components/TrustPanel";
import { SITE } from "../data/site";
import { automationReviews } from "../data/reviews";
import { buildBreadcrumbSchema, buildServiceSchema, buildWebPageSchema } from "../utils/schema";
import "./Services.css";

// Add these when you have examples ready
// const image1 = `${process.env.PUBLIC_URL}/assets/automationExample1.png`;
// const image2 = `${process.env.PUBLIC_URL}/assets/automationExample2.png`;
// const image3 = `${process.env.PUBLIC_URL}/assets/automationExample3.png`;
const heroBackgroundImage = `${process.env.PUBLIC_URL}/assets/Logo.svg`;

function ImageCarousel({ images }) {
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    if (!Array.isArray(images)) return;

    images.forEach((img) => {
      const preloader = new Image();
      preloader.src = img.image;
    });
  }, [images]);

  if (!images || images.length === 0) return null;

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  return (
    <Paper className="bws-gsl-carousel" elevation={0}>
      <div className="bws-gsl-carouselInner">
        <IconButton className="bws-gsl-carouselBtn" onClick={prev} aria-label="Previous example">
          <ChevronLeftIcon />
        </IconButton>

        <div className="bws-gsl-carouselFrame">
          {images.map((img, i) => (
            <img
              key={i}
              className={`bws-gsl-carouselImg ${i === idx ? "is-active" : "is-hidden"}`}
              src={img.image}
              alt={img.altText}
              loading="eager"
              decoding="async"
            />
          ))}
        </div>

        <IconButton className="bws-gsl-carouselBtn" onClick={next} aria-label="Next example">
          <ChevronRightIcon />
        </IconButton>
      </div>

      <div className="bws-gsl-carouselDots">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`bws-gsl-dot ${i === idx ? "is-active" : ""}`}
            aria-label={`View example ${i + 1}`}
            onClick={() => setIdx(i)}
          />
        ))}
      </div>
    </Paper>
  );
}

export default function Automation() {
  const safeReviews = Array.isArray(automationReviews) ? automationReviews : [];
  const title = "Automation & Integrations — Automate Reports, Sync Tools, Reduce Busywork";
  const description =
    "Automate repetitive tasks, connect tools, sync data, and streamline reporting workflows. Practical automation and integrations for businesses and individuals.";
  const canonical = "https://brainwavesolutions.net/services/automation";
  const structuredData = [
    buildWebPageSchema({ title, description, canonical }),
    buildBreadcrumbSchema([
      { name: "Home", url: SITE.url },
      { name: "Services", url: `${SITE.url}/services` },
      { name: "Automation & Integrations", url: canonical },
    ]),
    buildServiceSchema({
      name: "Automation & Integrations",
      description,
      canonical,
      offerLabel: "$150–$3,000+",
      serviceType: "Automation and integrations",
    }),
  ];

  const images = [
    // {
    //   image: image1,
    //   altText:
    //     "Workflow automation dashboard showing automated reporting steps, task statuses, and tool integrations.",
    // },
    // {
    //   image: image2,
    //   altText:
    //     "Business process automation example showing data flowing between apps and services.",
    // },
    // {
    //   image: image3,
    //   altText:
    //     "Operations automation workflow with scheduled tasks, alerts, and integration handoffs.",
    // },
  ].filter((img) => img.image);

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        structuredData={structuredData}
      />

      <Box className="bws-gsl-page">
        <Container maxWidth="lg">
          <div className="bws-gsl-hero">
            <div
              className="bws-gsl-heroMain"
              style={{ "--gsl-bg-image": `url(${heroBackgroundImage})` }}
            >
              <Typography variant="h1" className="bws-gsl-title">
                Automation and integrations that cut down busywork and keep things moving
              </Typography>

              <p className="bws-gsl-lead">
                A lot of repetitive work starts out feeling manageable — until it becomes part of every week. Copying
                data between tools, cleaning up the same exports, assembling the same reports, sending the same updates,
                and manually checking the same things over and over can quietly eat up a huge amount of time.
              </p>

              <p className="bws-gsl-lead bws-gsl-leadTight">
                I help businesses and individuals automate recurring tasks, connect tools, and build workflows that are
                more reliable and less manual. The goal is simple: fewer repetitive steps, fewer mistakes, and more
                time spent on work that actually matters.
              </p>

              <div className="bws-gsl-badges">
                <span className="bws-gsl-badge">Workflow automation</span>
                <span className="bws-gsl-badge">Tool integrations</span>
                <span className="bws-gsl-badge">Scheduled reporting</span>
                <span className="bws-gsl-badge">Less manual work</span>
              </div>

              <div className="bws-gsl-ctaRow">
                <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
                  Get help with automation
                </Button>
              </div>

              <div className="bws-gsl-miniTrust">
                <span>Businesses &amp; individuals</span>
                <span>•</span>
                <span>Clear communication</span>
                <span>•</span>
                <span>Practical solutions</span>
              </div>
            </div>

            <div className="bws-gsl-sideStack">
              <TrustPanel
                title="Work directly with Nate"
                body="You’re not sending your project into a big agency pipeline, you will work directly with me."
                badges={["Based in the USA", "Native English speaker"]}
              />

              <Paper className="bws-gsl-sideCard" elevation={0}>
                <Typography className="bws-gsl-sideTitle">Common automation problems I solve</Typography>
                <ul className="bws-gsl-sideList">
                  <li>Reports that have to be rebuilt manually every week or month</li>
                  <li>Data copied between tools by hand</li>
                  <li>Recurring exports that need cleanup before they can be used</li>
                  <li>Workflows that break because steps are inconsistent</li>
                  <li>Teams spending too much time on repeat admin work</li>
                </ul>

                <Typography className="bws-gsl-sideNote">
                  Best first step: send a message describing the steps you repeat now, the tools involved, and what you
                  wish happened automatically.
                </Typography>
              </Paper>
            </div>
          </div>

          <div className="bws-gsl-strip">
            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Reduce repetitive work</Typography>
              <Typography className="bws-gsl-stripText">
                Replace repeated manual steps with cleaner, more reliable workflows.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Connect the tools you already use</Typography>
              <Typography className="bws-gsl-stripText">
                Move data between systems more smoothly and reduce hand-entry and copy/paste.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Make reporting easier</Typography>
              <Typography className="bws-gsl-stripText">
                Automate recurring reporting steps so updates take less time and are easier to trust.
              </Typography>
            </Paper>
          </div>

          {images.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Examples:</Typography>
                <p className="bws-gsl-p">
                  Examples of automated workflows, connected tools, and reporting processes.
                </p>
              </div>

              <ImageCarousel images={images} />
            </div>
          )}

          <div className="bws-gsl-section">
            <div className="bws-gsl-grid">
              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">What I help with</Typography>
                  <ul className="bws-gsl-list">
                    <li>Workflow automation and cleanup</li>
                    <li>Tool-to-tool integrations and data syncing</li>
                    <li>Scheduled reports, alerts, and handoffs</li>
                    <li>Recurring export and import workflows</li>
                    <li>Reducing manual steps in operations and reporting</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">Good fit for</Typography>
                  <ul className="bws-gsl-list">
                    <li>Teams repeating the same process every week</li>
                    <li>Businesses juggling multiple disconnected tools</li>
                    <li>Operations and admin workflows with too many handoffs</li>
                    <li>Anyone who knows a process should be simpler than it is</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {safeReviews.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Client feedback</Typography>
              </div>

              <div className="bws-gsl-reviews">
                {safeReviews.slice(0, 3).map((r, idx) => (
                  <Paper key={idx} className="bws-gsl-review" elevation={0}>
                    <Rating
                      value={5}
                      readOnly
                      size="small"
                      className="bws-gsl-reviewStars"
                      aria-label="5 out of 5 stars"
                    />
                    <Typography className="bws-gsl-reviewQuote">“{r.quote}”</Typography>
                    <Typography className="bws-gsl-reviewAuthor">— {r.author}</Typography>
                  </Paper>
                ))}
              </div>
            </div>
          )}

          <div className="bws-gsl-section">
            <Paper className="bws-gsl-faq" elevation={0}>
              <Typography className="bws-gsl-h2">Frequently asked questions</Typography>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you help if I do not know exactly how the automation should work yet?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. In many cases, the first step is just clarifying the current process and identifying which parts
                  should be automated or simplified.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you connect data between the tools I already use?</Typography>
                <Typography className="bws-gsl-faqA">
                  Usually, yes. If the tools support exports, imports, APIs, or structured workflows, there is often a
                  good way to reduce the manual work between them.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">What do you need from me to get started?</Typography>
                <Typography className="bws-gsl-faqA">
                  A short description of the current workflow, the tools involved, and which steps you want to stop doing manually.
                </Typography>
              </div>
            </Paper>
          </div>

          <Paper className="bws-gsl-bottomCta" elevation={0}>
            <div>
              <Typography className="bws-ctaTitle">Need help automating a workflow?</Typography>
              <p className="bws-muted">
                Send a quick description of the steps you repeat now and what you wish happened automatically.
              </p>
            </div>
            <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
              Contact
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
